/**
 * platform-layout.tsx — Shell RIÊNG cho khu vực Platform (super-admin).
 *
 * Bao gồm guard riêng: nếu chưa đăng nhập platform → chuyển về /platform/login.
 * Không dùng chung SideNav / UserMenu của CRM (tránh lẫn danh tính người dùng).
 *
 * Điện thoại (< 768px): menu trái 240px cố định vỡ màn — thay bằng đầu trang có nút ☰ mở
 * NGĂN KÉO (SideDrawer, cùng component dùng cho "Cá nhân" ở app chính) chứa nguyên menu +
 * tài khoản; ngăn tự đóng khi chuyển trang. Nội dung tràn hết bề ngang, bỏ khung nổi bo góc
 * (vốn chừa chỗ cho sidebar bên cạnh, không hợp khi không còn sidebar).
 */
import { useEffect, useState } from 'react'
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LogOut, ShieldCheck, Moon, Sun, Monitor, Settings, ChevronDown, UserRound, Menu, type LucideIcon,
} from 'lucide-react'
import { usePlatformAuthStore } from '@/stores/platform-auth-store'
import { useUiStore, type ThemePref } from '@/stores/ui-store'
import { Loading } from '@/components/shared/feedback'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { SideDrawer, SideDrawerContent } from '@/components/ui/side-drawer'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { cn, initials } from '@/lib/utils'
import { useFeedbackUnread } from '@/hooks/use-platform-feedback'
import { unreadBadge } from '@/pages/platform/feedback-helpers'
import { AFFILIATE_NAV, AI_NAV, currentPageLabel, PLATFORM_NAV, SETTINGS_NAV, type NavItem } from './platform-nav'

const THEME_OPTIONS: { id: ThemePref; label: string; icon: LucideIcon }[] = [
  { id: 'light', label: 'Sáng', icon: Sun },
  { id: 'dark', label: 'Tối', icon: Moon },
  { id: 'system', label: 'Theo hệ thống', icon: Monitor },
]

/** `badge`: số hiện trên chấm đỏ bên phải (vd. góp ý mới); bỏ trống / 0 → không hiện. */
function NavRow({ item, nested, badge }: { item: NavItem; nested?: boolean; badge?: number }) {
  const Icon = item.icon
  const badgeText = unreadBadge(badge)
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
          nested && 'pl-9',
          isActive
            ? 'bg-card font-medium text-primary shadow-sm'
            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground',
        )
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      <span className="truncate">{item.label}</span>
      {badgeText && (
        <span
          className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-medium leading-none text-white"
          aria-label={`${badgeText} chưa xem`}
        >
          {badgeText}
        </span>
      )}
    </NavLink>
  )
}


/** Menu + tài khoản — dùng chung cho sidebar máy tính và ngăn kéo điện thoại. `onNavigate`: đóng ngăn kéo khi bấm một mục. */
function PlatformNavContent({
  feedbackUnread, admin, themePref, onTheme, onProfile, onLogout, onNavigate, mobile,
}: {
  feedbackUnread?: number
  admin: { fullName: string; email: string }
  themePref: ThemePref
  onTheme: (t: ThemePref) => void
  onProfile: () => void
  onLogout: () => void
  onNavigate?: () => void
  mobile?: boolean
}) {
  const location = useLocation()
  const inSettings = SETTINGS_NAV.some((i) => location.pathname.startsWith(i.to))
  const [settingsOpen, setSettingsOpen] = useState(inSettings)
  // Vào một trang trong nhóm Cài đặt (vd. từ link) → tự mở nhóm.
  useEffect(() => { if (inSettings) setSettingsOpen(true) }, [inSettings])

  return (
    <div
      className="flex min-h-0 flex-1 flex-col text-sidebar-foreground"
      onClickCapture={(e) => { if ((e.target as HTMLElement).closest('a')) onNavigate?.() }}
    >
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-2">
        {PLATFORM_NAV.map((item) => <NavRow key={item.to} item={item} badge={item.to === '/platform/feedback' ? feedbackUnread : 0} />)}

        <p className="px-3 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wide text-sidebar-foreground/50">
          AI
        </p>
        {AI_NAV.map((item) => <NavRow key={item.to} item={item} />)}

        <p className="px-3 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wide text-sidebar-foreground/50">
          Tiếp thị liên kết
        </p>
        {AFFILIATE_NAV.map((item) => <NavRow key={item.to} item={item} />)}

        <div className="pt-2">
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            aria-expanded={settingsOpen}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
              inSettings && !settingsOpen
                ? 'bg-card font-medium text-primary shadow-sm'
                : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground',
            )}
          >
            <Settings className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1 truncate text-left">Cài đặt</span>
            <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform', settingsOpen && 'rotate-180')} />
          </button>
          {settingsOpen && (
            <div className="mt-1 space-y-1">
              {SETTINGS_NAV.map((item) => <NavRow key={item.to} item={item} nested />)}
            </div>
          )}
        </div>
      </nav>

      {/* Tài khoản cá nhân — bấm tên để mở menu (giống trang khách) */}
      <div className="p-2">
        <PlatformUserMenu
          name={admin.fullName}
          email={admin.email}
          themePref={themePref}
          onTheme={onTheme}
          onProfile={onProfile}
          onLogout={onLogout}
          mobile={mobile}
        />
      </div>
    </div>
  )
}

export function PlatformLayout() {
  const { admin, loading, loadMe, logout } = usePlatformAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const { themePref, setThemePref } = useUiStore()
  const isMobile = useIsMobile()
  const [menuOpen, setMenuOpen] = useState(false)
  // Chấm đỏ góp ý mới (chỉ hỏi khi đã đăng nhập Platform).
  const { data: feedbackUnread } = useFeedbackUnread(!!admin)

  // Chuyển trang → tự đóng ngăn kéo (điện thoại).
  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  // Nạp profile platform một lần khi vào khu vực.
  useEffect(() => {
    if (!admin) loadMe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) return <Loading label="Đang tải..." className="h-screen" />
  if (!admin) return <Navigate to="/platform/login" state={{ from: location }} replace />

  const onProfile = () => navigate('/platform/account')
  const onLogout = async () => {
    await logout()
    navigate('/platform/login', { replace: true })
  }

  if (isMobile) {
    return (
      <div className="app-shell flex h-dvh flex-col bg-background pb-safe pl-safe pr-safe">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b px-3 pt-safe">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Mở menu"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg active:bg-muted"
          >
            <Menu className="h-5 w-5" />
          </button>
          <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">{currentPageLabel(location.pathname)}</span>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-4 py-4">
            <Outlet />
          </div>
        </main>

        <SideDrawer open={menuOpen} onOpenChange={setMenuOpen}>
          <SideDrawerContent title="Menu Platform Console">
            <div className="flex h-14 shrink-0 items-center gap-2 px-4 pt-safe">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <span className="text-sm font-bold tracking-tight">Platform Console</span>
            </div>
            <PlatformNavContent
              feedbackUnread={feedbackUnread}
              admin={admin}
              themePref={themePref}
              onTheme={setThemePref}
              onProfile={onProfile}
              onLogout={onLogout}
              onNavigate={() => setMenuOpen(false)}
              mobile
            />
          </SideDrawerContent>
        </SideDrawer>
      </div>
    )
  }

  return (
    <div className="app-shell flex h-screen bg-shell">
      {/* Sidebar riêng của platform */}
      <aside className="flex h-screen w-60 flex-col text-sidebar-foreground">
        <div className="flex h-14 items-center gap-2 px-4">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <span className="text-sm font-bold tracking-tight">Platform Console</span>
        </div>
        <PlatformNavContent
          feedbackUnread={feedbackUnread}
          admin={admin}
          themePref={themePref}
          onTheme={setThemePref}
          onProfile={onProfile}
          onLogout={onLogout}
        />
      </aside>

      {/* Nội dung */}
      <main className="my-2 mr-2 flex-1 overflow-y-auto rounded-xl bg-background shadow-[0_0_0_1px_hsl(var(--border))]">
        <div className="mx-auto max-w-[1400px] p-6">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

function PlatformUserMenu({
  name, email, themePref, onTheme, onProfile, onLogout, mobile,
}: {
  name: string
  email: string
  themePref: ThemePref
  onTheme: (t: ThemePref) => void
  onProfile: () => void
  onLogout: () => void
  /** Ngăn kéo điện thoại hẹp hơn — mở menu lên TRÊN thay vì sang phải (bên phải không đủ chỗ cho w-64). */
  mobile?: boolean
}) {
  const current = THEME_OPTIONS.find((t) => t.id === themePref) ?? THEME_OPTIONS[2]
  const ThemeIcon = current.icon
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full items-center gap-2.5 overflow-hidden rounded-lg px-1.5 py-1.5 text-left outline-none hover:bg-white/55 dark:hover:bg-white/5">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback>{initials(name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium leading-tight">{name}</p>
          <p className="truncate text-xs leading-tight text-muted-foreground">{email}</p>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={mobile ? 'top' : 'right'} align="end" sideOffset={8} className="w-64 p-1.5">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarFallback>{initials(name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{name}</p>
            <p className="truncate text-xs text-muted-foreground">{email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onProfile}>
          <UserRound className="text-muted-foreground" /> Cài đặt cá nhân
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <ThemeIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            <span className="flex-1">Giao diện</span>
            <span className="pl-2 pr-1 text-xs text-muted-foreground">{current.label}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-44">
            <DropdownMenuRadioGroup value={themePref} onValueChange={(v) => onTheme(v as ThemePref)}>
              {THEME_OPTIONS.map((t) => (
                <DropdownMenuRadioItem key={t.id} value={t.id}>{t.label}</DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <div className="px-1 pb-1 pt-1.5">
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center justify-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            <LogOut className="h-4 w-4" /> Đăng xuất
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
